/**
 * MapClusterPulse.jsx
 * Efeito pulse nos clusters (estilo GPSWOX)
 * Segue padrao do MapPulse.jsx existente
 *
 * Usa React Portal para renderizar elementos DOM sobre o mapa
 * CSS animations sao GPU-accelerated via transform
 */
import {
 useEffect, useState, useCallback, useRef, useMemo 
} from 'react';
import { createPortal } from 'react-dom';
import { map } from '../core/MapView';
import './MapClusterPulse.css';

// Tamanho do pulse baseado no zoom
const getPulseSize = (zoom, pointCount) => {
  // Base size ajustado pelo zoom
  let baseSize;
  if (zoom < 8) baseSize = 50;
  else if (zoom < 12) baseSize = 60;
  else baseSize = 70;

  // Ajuste pelo tamanho do cluster
  if (pointCount >= 100) return baseSize * 1.2;
  if (pointCount >= 10) return baseSize * 1.1;
  return baseSize;
};

const MapClusterPulse = ({ mapReady, sourceId, mapCluster }) => {
  const [clusters, setClusters] = useState([]);
  const [zoom, setZoom] = useState(10);
  const portalContainerRef = useRef(null);
  const rafRef = useRef(null);
  const mountedRef = useRef(true);
  const throttleRef = useRef(null);

  // Obter container do mapa para portal
  useEffect(() => {
    if (mapReady && map) {
      portalContainerRef.current = map.getContainer();
    }
  }, [mapReady]);

  // Atualizar posicoes dos clusters (com throttle e RAF para performance)
  const updateClusters = useCallback(() => {
    if (!mapReady || !map || !sourceId || !mountedRef.current) return;

    // Throttle: max 1 update por 50ms (20fps)
    if (throttleRef.current) return;
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
    }, 50);

    // Cancelar RAF anterior se existir
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      if (!mountedRef.current) return;

      try {
        const source = map.getSource(sourceId);
        if (!source) {
          setClusters([]);
          return;
        }

        // Buscar features de cluster visiveis
        const features = map.querySourceFeatures(sourceId, {
          filter: ['has', 'point_count'],
        });

        // Deduplicar clusters (querySourceFeatures pode retornar duplicados)
        const seen = new Set();
        const uniqueFeatures = features.filter((f) => {
          const id = f.properties.cluster_id;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        // Converter para posicoes na tela
        const currentZoom = map.getZoom();
        setZoom(currentZoom);

        const clusterData = uniqueFeatures.map((f) => ({
          id: f.properties.cluster_id,
          count: f.properties.point_count,
          x: Math.round(map.project(f.geometry.coordinates).x),
          y: Math.round(map.project(f.geometry.coordinates).y),
        }));

        setClusters(clusterData);
      } catch {
        // Silenciar erros (source pode nao existir ainda)
      }
    });
  }, [mapReady, sourceId]);

  // Listeners de eventos do mapa (otimizados)
  useEffect(() => {
    mountedRef.current = true;

    if (!mapReady || !map) return undefined;

    // Usar 'moveend' em vez de 'move' para melhor performance
    // 'render' e chamado apos cada frame renderizado
    const handleMoveEnd = () => updateClusters();
    const handleRender = () => updateClusters();

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);
    map.on('render', handleRender);

    // Update inicial
    updateClusters();

    return () => {
      mountedRef.current = false;
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
      map.off('render', handleRender);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, [mapReady, updateClusters]);

  // Memoizar elementos para evitar re-renders desnecessarios
  const pulseElements = useMemo(() => {
    if (!mapCluster || clusters.length === 0) return null;

    return clusters.map((cluster) => {
      const size = getPulseSize(zoom, cluster.count);
      return (
        <div
          key={cluster.id}
          className="cluster-pulse-container"
          style={{
            left: cluster.x,
            top: cluster.y,
            width: size,
            height: size,
          }}
        >
          <div
            className="cluster-pulse-ring"
            style={{ width: size, height: size }}
          />
        </div>
      );
    });
  }, [clusters, zoom, mapCluster]);

  // Nao renderizar se clustering desativado ou sem container
  if (!mapCluster || !portalContainerRef.current || !pulseElements) {
    return null;
  }

  return createPortal(
    <>{pulseElements}</>,
    portalContainerRef.current,
  );
};

MapClusterPulse.handlesMapReady = true;

export default MapClusterPulse;
